import {
    ApplicationRef,
    NgModule,
    PLATFORM_ID,
    REQUEST_CONTEXT,
    inject,
    makeStateKey,
    provideAppInitializer,
    TransferState,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
    provideClientHydration,
    withEventReplay,
    withNoHttpTransferCache,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { RouteReuseStrategy } from '@angular/router';
import { Response } from 'express';
import {
    RECAPTCHA_V3_SITE_KEY,
    RECAPTCHA_SETTINGS,
    RecaptchaV3Module,
    RecaptchaSettings,
} from 'ng-recaptcha';
import { TranslateService } from '@jht/brand-translate';
import { TranslateModule } from '@jht/brand-translate';
import { OptimizedImageModule } from '@jht/brand-directives';
import { providePageBuilder } from '@jht/brand-page-builder/core';
import { Observable } from 'rxjs';

import { AppRoutingModule } from './app-routing.module';
import { GraphQLModule } from './shared/graphql.module';

import { Config } from './shared/config';
import { Translations } from './shared/translations';
import { buildPageBuilderConfig } from './page-builder/page-builder.config';

import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { RedirectsService } from './shared/services/redirect.service';
import { VideoService } from './shared/services/video.service';
import { Vision2RouteReuseStrategy } from './route-reuse-strategy';
import { WINDOW } from './shared/models/tokens';
import {
    RequestContext,
    SiteConfigurationData,
    siteData,
} from './shared/utils/models';
import { MetaService } from './shared/services/meta.service';
import { ContentService } from './shared/content';
import { NavigationService } from './core/components/navigation/navigation.service';
import { SchemaService } from './shared/services/schema.service';

const SITE_CONFIG_KEY = makeStateKey<SiteConfigurationData>('siteConfig');

export class HeaderInterceptor implements HttpInterceptor {
    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<HttpEvent<any>> {
        const config = inject(Config);
        const document = inject(DOCUMENT);
        const setConfigHeaders = (req: HttpRequest<any>) => {
            const language = config.getCurrentLanguage();
            const region = config.getSbu();
            return req.clone({
                setHeaders: {
                    ['x-request-config']: JSON.stringify({
                        site: siteData.site,
                        region: region,
                        language: language,
                        host: document.location.host,
                    }),
                },
            });
        };
        return next.handle(setConfigHeaders(req));
    }
}

const appInit = async () => {
    const redirectService = inject(RedirectsService);
    const context: RequestContext = inject(REQUEST_CONTEXT) as RequestContext;
    const config = inject(Config);
    const contentService = inject(ContentService);
    const document = inject(DOCUMENT);
    const platformId = inject(PLATFORM_ID);
    const transferState = inject(TransferState);
    const navService = inject(NavigationService);
    const translations = inject(Translations);
    const metaService = inject(MetaService);

    let path: string | undefined;
    let response: Response | undefined;

    if (isPlatformServer(platformId) && context?.siteConfig) {
        config.language = context.language;
        config.region = context.region;
        path = context.path;
        response = context.response;
        config.init(context.siteConfig.sites, context.siteConfig.environment);
        transferState.set(SITE_CONFIG_KEY, context.siteConfig);
    } else if (isPlatformBrowser(platformId)) {
        const urlLanguageMatch = document.location.pathname.match(
            '^(?:/)?([A-Za-z]{3})(?:/|$)',
        );
        config.language = urlLanguageMatch?.[1] ?? siteData.defaultLanguage;
        config.region = siteData.defaultRegion;
        const configData = transferState.get(SITE_CONFIG_KEY, null);
        if (configData) {
            config.init(configData.sites, configData.environment);
        }
    } else {
        return;
    }
    const schemaService = inject(SchemaService);
    metaService.init();

    await Promise.all([
        contentService.loadSitePages(),
        navService.load(contentService),
        translations.load(),
        redirectService.load(),
    ]);

    schemaService.addOrganizationSchema();

    if (response !== undefined && path !== undefined) {
        redirectService.serverRedirect(response, path);
    }
};

export function setupTranslateConfig(
    configService: Config,
    translations: Translations,
) {
    return {
        siteConfig: {
            fallbackLanguages: configService.getFallbackLanguages(),
            regionLanguage: configService.getLanguageRegion(),
        },
        translations: {
            dictionary: translations.dictionary,
        },
    };
}

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        BrowserAnimationsModule,
        CoreModule,
        GraphQLModule,
        OptimizedImageModule,
        TranslateModule,
        RecaptchaV3Module,
    ],
    exports: [],
    providers: [
        /** Needed for Brand Angular Library */
        {
            provide: 'WINDOW',
            useValue: WINDOW,
        },
        provideAppInitializer(appInit),
        Config,
        Translations,
        {
            provide: 'translateConfig',
            useFactory: setupTranslateConfig,
            deps: [Config, Translations],
        },
        TranslateService,
        {
            provide: RECAPTCHA_SETTINGS,
            useValue: {
                siteKey: '6LcR0SApAAAAAFptiaK69fb1hRKdsKRYABR9KEtr',
            } as RecaptchaSettings,
        },
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useValue: '6LcZ_3soAAAAALran-vOnEriPYBfdYe514ZCMVKc',
        },
        {
            provide: RouteReuseStrategy,
            useClass: Vision2RouteReuseStrategy,
        },
        VideoService,
        RedirectsService,
        provideHttpClient(withInterceptorsFromDi(), withFetch()),
        provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderInterceptor,
            multi: true,
        },
        providePageBuilder(() =>
            buildPageBuilderConfig(
                inject(Config),
                inject(DOCUMENT),
                inject(HttpClient),
            ),
        ),
    ],
})
export class AppModule {
    constructor() {}
}
